<template>
    <div class="field--autocomplete">
        <div
            :class="[
                'field--autocomplete__wrapper',
                isListOpen ? 'expanded' : '',
                model.length ? 'valid' : '',
                isEmpty ? 'has-error' : '',
                hasStoreError ? 'has-error' : '',
            ]"
        >
            <Field
                v-model="model"
                :name="name"
                :type="type"
                :disabled="disabled"
                :handles-error="handlesError"
                :placeholder="placeholder"
                :handles-data-preload="handlesDataPreload"
                :suggestions="false"
                :is-validated="false"
                :label-shows-valid="false"
                :street-filter="true"
                @blur="blur($event)"
                @focus="focus"
                @click="open"
                @input="input($event)"
                @is-valid="setIsValid($event)"
                @data-preload-complete="filterItemList($event)"
            >
                <slot />
            </Field>
            <ItemList
                key="itemList"
                :is-default-open="isListOpen"
                :item-list="displayedItemList"
                class="fs-13"
                @select-option="selectValue($event.value)"
            />
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import Field from '@/components/basic/fields/Field.vue';
import ItemList from '@/components/basic/ItemList.vue';
import type { FieldInterface } from '@/types';
import { UtilityHelper } from '@/helpers/UtilityHelper';

export default defineComponent({
    name: 'AutocompleteField',
    components: {
        Field,
        ItemList,
    },
    props: {
        type: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            default: '',
        },
        modelValue: {
            type: String,
            default: '',
        },
        itemList: {
            type: Array,
            default: (): FieldInterface[] => [],
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        handlesError: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: '',
        },
        isValidated: {
            type: Boolean,
            default: true,
        },
        handlesDataPreload: {
            type: Boolean,
            default: true,
        },
        overrideOldValue: {
            type: Boolean,
            default: false,
        },
        isStreet: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'focus', 'blur', 'input'],
    setup(props, { emit }) {
        const store = useStore();

        const displayedItemList = ref<FieldInterface[]>([]);
        const model = ref<string>(props.modelValue || '');
        const isListOpen = ref(false);
        const ignoreNextOpen = ref(false);
        const isClosing = ref(false);
        const isValid = ref(false);
        const isEmpty = ref(false);
        const hasStoreError = computed(() =>
            Boolean(store.state.errors.get(props.name))
        );

        function getItemList() {
            const items = props.itemList as FieldInterface[];
            if (
                !props.isStreet &&
                items.length === 1 &&
                (!model.value ||
                    model.value.length === 0 ||
                    props.overrideOldValue)
            ) {
                setValue(String(items[0].value), true);
            }
            return items;
        }

        const list = computed(() => getItemList());

        watch(
            () => props.itemList,
            () => {
                displayedItemList.value = list.value;
            },
            { immediate: true }
        );

        watch(
            () => props.modelValue,
            (newVal) => {
                model.value = newVal || '';
                filterItemList(model.value);
            }
        );

        watch(
            () => model.value,
            (newVal, oldVal) => {
                if (!props.isValidated) return;
                if (!newVal || newVal === oldVal) return;
                if (isListOpen.value || isClosing.value) return;
                if (
                    store.state.validFields &&
                    store.state.validFields.has(props.name)
                )
                    return;

                validate();
            }
        );

        watch(
            () => model.value,
            (val) => {
                emit('update:modelValue', val);
            }
        );

        function focus(): void {
            emit('focus');
            open();
        }

        function blur(value: any): void {
            isEmpty.value = !value.length;
            UtilityHelper.sleep(60).then(() => {
                close();
            });
        }

        function validate() {
            if (props.isValidated) {
                store.dispatch('validate', {
                    name: props.name,
                    value: model.value,
                });
            }
            emit('blur', model.value);
        }

        function open(): void {
            if (ignoreNextOpen.value) {
                ignoreNextOpen.value = false;
            } else {
                isListOpen.value = true;
            }
        }

        function close(): void {
            const time = Date.now();
            isClosing.value = true;
            UtilityHelper.wait(() => {
                return (
                    model.value.length > 0 ||
                    (Date.now() - time > 60 && model.value.length === 0)
                );
            }).then(() => {
                isListOpen.value = false;
                isClosing.value = false;
                validate();
            });
        }

        function selectValue(value: string): void {
            UtilityHelper.wait(() => setValue(value, true)).then(() => {
                close();
            });
        }

        function input(eventOrValue: string | Event): void {
            let value: string;

            if (typeof eventOrValue === 'string') {
                value = eventOrValue;
            } else if (eventOrValue instanceof Event) {
                const target = eventOrValue.target as HTMLInputElement | null;
                value = target ? target.value : '';
            } else {
                value = '';
            }

            filterItemList(value);
            if (!isListOpen.value) {
                open();
            }
            setValue(value);
        }

        async function setValue(
            value: string,
            preventOpen = false
        ): Promise<boolean> {
            ignoreNextOpen.value = preventOpen;
            model.value = value;
            emit('input', model.value);
            emit('update:modelValue', model.value);
            return true;
        }

        function filterItemList(value: string): void {
            UtilityHelper.wait(() => !isClosing.value).then(() => {
                displayedItemList.value = list.value.filter((item) =>
                    item.value
                        .toString()
                        .toLowerCase()
                        .includes(value.toLowerCase())
                );
            });
        }

        function setIsValid(valid: boolean): void {
            isValid.value = valid;
        }

        return {
            displayedItemList,
            model,
            isListOpen,
            ignoreNextOpen,
            isClosing,
            isValid,
            isEmpty,
            list,
            hasStoreError,
            focus,
            blur,
            validate,
            open,
            close,
            selectValue,
            input,
            setValue,
            filterItemList,
            setIsValid,
        };
    },
});
</script>
